import type { AssetContainer } from '@babylonjs/core/assetContainer';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import type { Scene } from '@babylonjs/core/scene';
import type { Skeleton } from '@babylonjs/core/Bones/skeleton';
import type { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { FresnelParameters } from '@babylonjs/core/Materials/fresnelParameters';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { VertexBuffer } from '@babylonjs/core/Buffers/buffer';

import { loadGlbContainer } from './gltf';
import type { Rig } from './rig';

/**
 * A skinned body bound to the procedural rig.
 *
 * ── Why the rig stays ───────────────────────────────────────────────────────────────────
 *
 * The obvious move is to throw away `rig.ts`'s `TransformNode` hierarchy and animate Babylon
 * bones directly. That would mean rewriting `poses.ts` — eight movements, all tested, whose
 * separation is asserted at 0.35 rad — against a different API, for no behavioural gain.
 *
 * So the rig is untouched and stays the authority: `poses.ts` writes rotations into the same
 * seven `TransformNode`s it always has, and `sync()` copies those rotations onto the bones of
 * the same name immediately afterwards. One call, in one place, right after `applyPose`.
 *
 * **Not `Bone.linkTransformNode`**, which is the API that looks made for this. It makes the
 * bone adopt the node's whole transform, *including its position* — and the two disagree by
 * design: `rig.ts` puts a shoulder at Y 1.280 because that is where its box shoulder was, while
 * the mesh's real shoulder is at 1.360. Linking would drag the shoulder vertices 8 cm down the
 * chest. Rotation is the only channel the pose actually means.
 */
export interface SkinnedBody {
  /** The skinned mesh, parented under the rig's root. */
  readonly mesh: Mesh;
  /** Copies the rig's joint rotations onto the bones. Call after `applyPose`. Allocates nothing. */
  sync(): void;
  /**
   * Moves the rig's arm joints onto the mesh's own shoulder, elbow and wrist, so what the hands
   * hold hangs off the mesh's hands. **Call once, after the collider is measured.** See
   * `alignArms`.
   */
  alignArms(): void;
  /**
   * Moves `rig.head` onto the mesh's own head bone and returns the vertices the head bone carries,
   * in `rig.head`'s frame at rest — the shape a helmet must sit over. Empty when the skin has no
   * head bone. **Call once, after the collider is measured**, for the same reason as `alignArms`.
   */
  alignHead(): Vector3[];
  dispose(): void;
}

/**
 * The eleven joints, by the name they carry in **both** worlds.
 *
 * `scripts/rig-glb.py` writes exactly these into the `.glb`'s skin, so a rename here without a
 * re-run leaves a bone unfound — which is why `attachSkinnedBody` reports what it could not
 * bind instead of silently posing ten of eleven joints.
 *
 * **Seven until Phase 28.1**, and the four added are the elbows and knees. `poses.ts` bends an
 * elbow up to 1.4 rad in an attack; a body with no elbow bone kept its arm straight while the
 * sword and shield — which hang off the rig's forearm — followed the bend and left the hand.
 */
export const SKINNED_JOINTS = [
  'body', 'torso', 'head', 'armR', 'armL', 'legR', 'legL',
  'foreR', 'foreL', 'shinR', 'shinL',
] as const;

/**
 * The rig joints `alignArms` moves, each under the parent it is measured from. The `hand*` bones
 * are **markers** the rigger writes with no vertices: they are there to say where the wrist is.
 */
const ARM_CHAIN = [
  ['armR', 'torso'],
  ['armL', 'torso'],
  ['foreR', 'armR'],
  ['foreL', 'armL'],
  ['handR', 'foreR'],
  ['handL', 'foreL'],
] as const;

export type SkinnedJoint = (typeof SKINNED_JOINTS)[number];

/**
 * Where the rigged bodies live, keyed by what asks for one.
 *
 * **One whole body per chest class** (Phase 28.1): the chest already decides the body and a change
 * of chest already rebuilds the model (ADR 0016), so a class is a different mesh rather than a
 * shell over a shared one. They share nothing but bone *names* — each was rigged off its own
 * silhouette — which is all `sync` needs, since it copies rotations by name. `player` is the
 * un-armoured body, worn with no chest at all.
 *
 * **No `vendor` body** since 2026-09-28: every non-mob was always dressed as `player`, and there are
 * no vendor *entities* — the shopkeepers are scenery, and since Phase 42 painted props of their own
 * (`models/props.ts`). `npc.glb` was 0.42 MB fetched at every login and never drawn.
 */
export const BODY_URLS = {
  player: new URL('../../assets/models/hero.glb', import.meta.url).href,
  CLOTH: new URL('../../assets/models/hero-cloth.glb', import.meta.url).href,
  LEATHER: new URL('../../assets/models/hero-leather.glb', import.meta.url).href,
  PLATE: new URL('../../assets/models/hero-plate.glb', import.meta.url).href,
} as const;

export type BodyKind = keyof typeof BODY_URLS;

const mob = (file: string): string => new URL(`../../assets/models/${file}.glb`, import.meta.url).href;

/**
 * A monster's painted body, by the catalogue `mesh_id` its type draws with (Phase 28.6, ADR 0019).
 *
 * **One body per `mesh_id`, not per monster type**: `mobs.yaml` already shares a mesh between types
 * (three kinds of cutthroat, two ghouls), and the file is the look. A `mesh_id` not listed here keeps
 * its procedural rig — the three bosses today, whose models are not T-poses the rigger can read.
 *
 * Built by `scripts/build-body.sh` at **the mob budget** (`MOB_BODY_TRIS`, 512² texture): a
 * procedural monster is already 1,720–2,780 triangles in 18–27 meshes, so a painted one costs the
 * same triangles in one draw call.
 */
export const MOB_BODY_URLS: Readonly<Record<string, string>> = {
  mesh_skeleton_warrior: mob('mob_skeleton_warrior'),
  mesh_skeleton_archer: mob('mob_skeleton_archer'),
  mesh_ghoul: mob('mob_ghoul'),
  mesh_mummy: mob('mob_mummy'),
  mesh_wight: mob('mob_wight'),
  mesh_wraith: mob('mob_wraith'),
  mesh_bone_mage: mob('mob_bone_mage'),
  mesh_dread_knight: mob('mob_dread_knight'),
  mesh_human_cutthroat: mob('mob_human_cutthroat'),
  mesh_imp: mob('mob_imp'),
};

/**
 * **A rim of cool light round a monster's silhouette** (2026-09-29). Reported: *contrast with
 * enemies is not really good* — the painted monsters are black robes and dark armour on a dark
 * floor. A Fresnel emissive lights only the edges turning away from the camera, so the outline of a
 * body separates from the ground without flattening the painted surface the way a brighter
 * self-light would. Set once per monster type, on the material the whole type shares. Chosen.
 */
export const MOB_RIM = { colour: [0.42, 0.5, 0.62] as const, power: 2.2, bias: 0.05 };

/** Gives a monster body's shared materials the rim light (`MOB_RIM`). */
export function addMobRim(container: AssetContainer): void {
  for (const material of container.materials) {
    if (!(material instanceof StandardMaterial)) continue;
    const rim = new FresnelParameters();
    rim.bias = MOB_RIM.bias;
    rim.power = MOB_RIM.power;
    rim.leftColor = new Color3(...MOB_RIM.colour);
    rim.rightColor = Color3.Black();
    material.emissiveFresnelParameters = rim;
  }
}

/** The triangle budget a monster's body is built to (ADR 0019). `skinned.test.ts` holds each file to it. */
export const MOB_BODY_TRIS = 2000;

/**
 * The body a character wears for its chest class: that class's own when it loaded, else the
 * un-armoured one. A class whose asset failed degrades to a body rather than to boxes, which is
 * the rule every other failure here follows one level down.
 */
export function bodyKindFor(armour: string | undefined, loaded: ReadonlySet<BodyKind>): BodyKind {
  if ((armour === 'CLOTH' || armour === 'LEATHER' || armour === 'PLATE') && loaded.has(armour)) {
    return armour;
  }
  return 'player';
}

/**
 * Whether a body brings its own surface. A textured body is a painted character — face, skin,
 * cloth and all — and dressing it in the box chest's flat material would paint over all of it.
 */
export function isTextured(mesh: Mesh): boolean {
  return (mesh.material?.getActiveTextures().length ?? 0) > 0;
}

/**
 * Loads a rigged body once, as a container to clone from.
 *
 * A container rather than a straight import because there is one mesh per *entity* and the
 * asset is the same for all of them: `instantiateModelsToScene` gives each clone its own
 * skeleton, which is what lets two players stand in different poses. Importing per entity
 * would re-parse up to 0.9 MB every time somebody walks into the zone.
 *
 * Returns `null` rather than throwing on a load failure. A missing body must degrade to the
 * procedural rig, not to a black screen — the same rule the pixelate pass follows, and for the
 * same reason: a cosmetic upgrade that can take the whole character with it is worse than no
 * upgrade.
 */
export async function loadBodyContainer(scene: Scene, url: string): Promise<AssetContainer | null> {
  const container = await loadGlbContainer(scene, url);
  if (!container) console.error(`grind: skinned body failed to load (${url}), falling back to the box rig`);
  return container;
}

/**
 * One joint's wiring: the rig node the pose is written to, and the node the bone reads from.
 *
 * ── The thing that made this hard ───────────────────────────────────────────────────────
 *
 * **A bone imported from glTF cannot be posed by writing to the bone.** Babylon's glTF loader
 * builds a `TransformNode` per glTF node and calls `bone.linkTransformNode(node)`; from then on
 * `Skeleton.prepare()` — which runs every frame — *overwrites each bone's local matrix from its
 * linked node.* So `bone.setRotation`, the `bone.rotation` setter, `bone.setRotationMatrix`,
 * `bone.updateMatrix` and even copying straight into `bone.getLocalMatrix()` all appear to work,
 * read back correctly, and are erased before anything is drawn. Measured: **0 of 5853 vertices
 * moved**, with the bone's local matrix a pure translation and the skeleton's transform for that
 * bone the identity.
 *
 * `target` is therefore the *linked* node where there is one, and the bone's own transform node
 * otherwise. Writing a rotation there is what the skinning path reads.
 */
interface Link {
  /** The rig joint `poses.ts` animates. Read from. */
  node: TransformNode;
  /** What the bone actually derives its local matrix from. Written to. */
  target: TransformNode;
}

/**
 * Clones a loaded body, parents it under `rig.root`, and wires its bones to the rig's joints.
 *
 * Returns `null` when the container holds no skinned mesh — a `.glb` exported without a skin
 * loads perfectly and animates not at all, which is indistinguishable from a broken pose
 * system unless something says so here.
 */
export function attachSkinnedBody(
  container: AssetContainer,
  rig: Rig,
  name: string,
): SkinnedBody | null {
  const entries = container.instantiateModelsToScene((source) => `${name}-${source}`, false);
  const mesh = entries.rootNodes
    .flatMap((node) => [node, ...node.getChildMeshes()])
    .find((node): node is Mesh => (node as Mesh).skeleton != null);

  if (!mesh?.skeleton) {
    console.error(`grind: ${name} has no skinned mesh — the .glb carries no skin`);
    entries.dispose();
    return null;
  }

  const skeleton: Skeleton = mesh.skeleton;
  const links: Link[] = [];
  const missing: string[] = [];
  for (const joint of SKINNED_JOINTS) {
    const bone = skeleton.bones.find((b) => b.name === joint);
    const node = rig[joint];
    if (!bone || !node) {
      missing.push(joint);
      continue;
    }
    // The linked node is the one the skeleton reads; a bone with none (a skeleton built in code
    // rather than imported) is posed through its own transform node.
    const target = bone.getTransformNode();
    if (!target) {
      missing.push(`${joint} (no transform node)`);
      continue;
    }
    // **Clear the quaternion or the euler rotation is ignored.** The glTF loader sets
    // `rotationQuaternion` on every node it builds, and Babylon uses the quaternion *in
    // preference to* `rotation` whenever it is non-null — so copying Euler angles onto a node
    // that has one writes to a field nothing reads. It is the second silent-no-op in this file's
    // history; the first was writing to the bone at all.
    target.rotationQuaternion = null;
    links.push({ node, target });
  }
  if (missing.length > 0) {
    // Loud, and not fatal: six of seven joints animating is a subtler fault than none of
    // them, and the names are the contract with `scripts/rig-glb.py`.
    console.error(
      `grind: ${name} skeleton is missing ${missing.join(', ')} — ` +
        `re-run scripts/rig-glb.py, or the bone names have drifted from rig.ts`,
    );
  }

  // Each bone's rest position, in the mesh's own frame — which is `rig.root`'s, the mesh being
  // parented there at identity below. **Read off the inverse bind matrix, not the bone's node**:
  // the glTF loader hangs the nodes under a `__root__` that flips handedness, so a node's world
  // position puts `armR` at +x while the vertices it moves are at −x. The bind matrices are in the
  // mesh's frame, which is the one the vertices are drawn in.
  const heads = new Map<string, Vector3>();
  for (const bone of skeleton.bones) {
    heads.set(bone.name, bone.getAbsoluteInverseBindMatrix().getTranslation().scaleInPlace(-1));
  }

  // The mesh hangs off the root, **not off `rig.body`**: the bones already carry the whole
  // body's motion, so parenting to an animated node would apply the bob twice.
  mesh.parent = rig.root;
  mesh.position.setAll(0);
  mesh.rotation.setAll(0);

  return {
    mesh,
    /**
     * Copies each rig joint's rotation onto the node its bone reads from.
     *
     * Three Euler numbers per joint, seven joints — and no `Skeleton.prepare()` call here: the
     * scene does that once a frame, and it is the step that pulls these nodes into the bones.
     * Calling it per entity would recompute every skeleton in the zone once per character.
     *
     * **The guard against the failure described on `Link` is in `skinned.test.ts`, and it asserts
     * on vertex positions** through `getPositionData(true, …)`. A bone's stored rotation is not
     * evidence of anything: the version of this that wrote to bones passed a rotation assertion
     * while deforming nothing at all.
     */
    sync(): void {
      for (const link of links) {
        link.target.rotation.copyFrom(link.node.rotation);
      }
    },

    /**
     * ── Why the rig's arms move and the mesh's do not ──────────────────────────────────────
     *
     * Everything a hand holds hangs off the **rig**: the weapon off `handR`, a shield off
     * `foreL`, the cast glow off `handL`. The rig's shoulder is at ±0.340 because that is where the
     * box shoulder was, and the mesh's is at ±0.203 — so the shield stood 0.14 units clear of the
     * forearm. Moving the three arm joints of each side onto the mesh's own joints puts every held
     * item in the mesh's hand at once, without any of them knowing a mesh exists.
     *
     * **After the collider, not before.** `finaliseParts` measures the collider from `rig.parts`,
     * and the hidden box arms hang off these joints: moved first, a skinned player's collider
     * would be narrower than a boxed one's. ADR 0006 holds the two identical.
     *
     * A body without the `hand*` markers (rigged before Phase 28.1) is left alone: there is no
     * wrist to align to, and the old offsets are what it was built against.
     */
    alignArms(): void {
      if (!heads.has('handR') || !heads.has('handL')) return;
      const torso = rig.body.position.add(rig.torso.position);
      for (const [joint, parent] of ARM_CHAIN) {
        const head = heads.get(joint);
        const from = parent === 'torso' ? torso : heads.get(parent);
        if (!head || !from) continue;
        rig[joint].position.copyFrom(head.subtract(from));
      }
    },

    /**
     * The rig's head is where the box skull sat, and the mesh's head bone is where the painted
     * head pivots. A helmet hung off the first while the second turns would slide off the head as
     * the torso bends, so the rig joint moves onto the bone — the skull box it carries is hidden
     * on a textured body, which is the only one that wears generated gear.
     */
    alignHead(): Vector3[] {
      const head = heads.get('head');
      const index = skeleton.bones.findIndex((b) => b.name === 'head');
      if (!head || index < 0) return [];
      rig.head.position.copyFrom(head.subtract(rig.body.position.add(rig.torso.position)));

      const positions = mesh.getVerticesData(VertexBuffer.PositionKind);
      const joints = mesh.getVerticesData(VertexBuffer.MatricesIndicesKind);
      const weights = mesh.getVerticesData(VertexBuffer.MatricesWeightsKind);
      if (!positions || !joints || !weights) return [];
      const out: Vector3[] = [];
      for (let v = 0; v < positions.length / 3; v++) {
        let w = 0;
        for (let k = 0; k < 4; k++) if (joints[v * 4 + k] === index) w += weights[v * 4 + k]!;
        if (w > 0.5) out.push(new Vector3(positions[v * 3]! - head.x, positions[v * 3 + 1]! - head.y, positions[v * 3 + 2]! - head.z));
      }
      return out;
    },

    dispose(): void {
      // **`entries.dispose()` alone leaks the body.** It disposes the container's *root* nodes,
      // and re-parenting the mesh to `rig.root` above took it out of that list — so the clone
      // survived, invisible, holding its geometry. A skeleton is not a child of anything either,
      // so it needs saying too. Caught by asserting `mesh.isDisposed()` rather than trusting the
      // call: nothing errors when a dispose disposes less than it looks like it does.
      mesh.skeleton?.dispose();
      mesh.dispose(false, false);
      entries.dispose();
    },
  };
}
